import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchOrders, updateOrderStatus, assignRider, updatePaymentStatus, orderUpdated } from './ordersSlice';
import { refreshAnalytics } from '../analytics/analyticsSlice';
import Shell from '../dashboard/Shell';
import RefundModal from '../../components/RefundModal';
import SendPaymentLinkPopover from '../sales-tools/SendPaymentLinkPopover';
import PaymentLinksHistory from '../sales-tools/PaymentLinksHistory';
import axios from '../../axiosConfig';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useToast } from '../../components/ui/Toast';
import { usePermission } from '../roles/RoleGuard';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { getOrderTaxName } from '../../utils/merchantTax';
import './OrderDetailsPage.css';

const STATUS_OPTIONS = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];
const PAYMENT_OPTIONS = ['paid', 'unpaid'];
const DISPLAY_STATUS_OPTIONS = [...STATUS_OPTIONS, 'refunded'];

const getStatusClass = (status) => {
  const s = (status || '').toLowerCase();
  return DISPLAY_STATUS_OPTIONS.includes(s) ? s : 'pending';
};
const getStatusLabel = (s) => (!s ? 'Pending' : s.charAt(0).toUpperCase() + s.slice(1).toLowerCase());
const getPaymentClass = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'paid') return 'paid';
  if (s === 'refunded') return 'refunded';
  return 'unpaid';
};

const formatVariantDisplay = (item) => {
  if (item?.variantCombination && typeof item.variantCombination === 'object' && Object.keys(item.variantCombination).length > 0) {
    return Object.entries(item.variantCombination).map(([k, v]) => `${k}: ${v}`).join(', ');
  }
  return item?.variantName || null;
};

const formatUnitInfo = (item, formatMoney) => {
  if (!item?.unitOfMeasure || item.unitOfMeasure === 'PIECE' || !item?.unitQuantity) return null;
  const unitShortNames = {
    KG: 'kg', G: 'g', LB: 'lb', OZ: 'oz',
    METER: 'm', YARD: 'yd', FT: 'ft', CM: 'cm', INCH: 'in',
    LITER: 'L', ML: 'ml', GALLON: 'gal',
    SQM: 'sqm', SQFT: 'sqft', PIECE: 'pc'
  };
  const unitShort = unitShortNames[item.unitOfMeasure] || item.unitOfMeasure.toLowerCase();
  const unitQty = parseFloat(item.unitQuantity);
  const pricePerUnit = item.pricePerUnit ? parseFloat(item.pricePerUnit) : item.price;
  return `${unitQty} ${unitShort} @ ${formatMoney(pricePerUnit)}/${unitShort}`;
};

export default function OrderDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const token = useSelector(s => s.auth.token);
  const { formatInDisplayCurrency, accountCurrency } = useCurrency();
  const formatMoney = (amount) => formatInDisplayCurrency(amount, accountCurrency);

  // Finance — gate the Profitability block on TWO checks:
  // 1) permission: cashiers/sales reps don't see gross-profit numbers
  //    (Owners get it via wildcard, MANAGER by explicit grant)
  // 2) feature access: if the admin disables finance.overview for this
  //    merchant's tier, the block hides everywhere — keeps gating
  //    consistent with the Finance page itself.
  const hasFinancePerm = usePermission('finance:view');
  const canManageOrders = usePermission('orders:manage');
  const { hasAccess: financeFeatureOn, loading: financeLoading } = useFeatureAccess('finance.overview');
  const canSeeProfitability = hasFinancePerm && !financeLoading && financeFeatureOn;

  const { items = [], appointments = [], status: listStatus } = useSelector(s => s.orders);

  // Locate the order from Redux store (orders + appointments). Refresh if not found.
  const order = useMemo(() => {
    const ord = items.find(o => o.id === id);
    if (ord) return { ...ord, type: ord.type || 'order' };
    const appt = appointments.find(a => a.id === id);
    if (appt) {
      const taxAmount = parseFloat(appt.paymentData?.taxAmount) || 0;
      const servicePrice = parseFloat(appt.servicePrice || 0);
      const serviceCharge = parseFloat(appt.serviceCharge || 0);
      return {
        id: appt.id,
        type: 'appointment',
        customer: {
          firstName: appt.customerName?.split(' ')[0] || 'Appointment',
          lastName: appt.customerName?.split(' ').slice(1).join(' ') || 'Customer',
          email: appt.customerEmail || '',
          phone: appt.customerPhone || ''
        },
        orderItems: [{
          product: {
            name: appt.service?.name || appt.serviceName || 'Appointment Service',
            primaryImage: appt.service?.primaryImage
          },
          quantity: 1,
          price: servicePrice
        }],
        subtotal: servicePrice,
        serviceCharge,
        taxTotal: taxAmount,
        taxName: appt.paymentData?.taxSettings?.name || 'Tax',
        total: parseFloat(appt.totalPrice) || (servicePrice + serviceCharge + taxAmount),
        status: appt.status || 'CONFIRMED',
        paymentStatus: appt.paymentStatus || 'PAID',
        createdAt: appt.scheduledAt || appt.createdAt,
        appointmentDate: appt.appointmentDate,
        appointmentTime: appt.appointmentTime
      };
    }
    return null;
  }, [items, appointments, id]);

  // If the order isn't in store yet, trigger a fetch.
  useEffect(() => {
    if (!order && listStatus !== 'loading') {
      dispatch(fetchOrders({ page: 1 }));
    }
  }, [order, listStatus, dispatch]);

  const [localOrder, setLocalOrder] = useState(null);
  useEffect(() => {
    if (order && order.id) setLocalOrder(order);
  }, [order]);

  // Dropdowns
  const [statusOpen, setStatusOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Rider state
  const [showRiderForm, setShowRiderForm] = useState(false);
  const [riderInfo, setRiderInfo] = useState({
    name: '', phone: '', notes: '', company: '', trackingCode: '', trackingUrl: ''
  });

  // Chowdeck
  const [dispatchingChowdeck, setDispatchingChowdeck] = useState(false);
  const [dispatchingTerminal, setDispatchingTerminal] = useState(false);
  const [refreshingTerminal, setRefreshingTerminal] = useState(false);
  const [terminalError, setTerminalError] = useState(null);
  const [chowdeckError, setChowdeckError] = useState(null);

  // Invoice & email
  const [invoiceLinks, setInvoiceLinks] = useState(null);
  const [invoiceLinksLoading, setInvoiceLinksLoading] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(null);
  const [emailError, setEmailError] = useState(null);

  // Refund modal
  const [showRefundModal, setShowRefundModal] = useState(false);

  // Phase 3.3 — Send-payment-link popover anchored to the topbar
  // button. historyKey bumps after a successful create so the inline
  // PaymentLinksHistory re-fetches.
  const [paymentLinkAnchor, setPaymentLinkAnchor] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  useEffect(() => {
    const fetchLinks = async () => {
      if (!order?.id) return;
      if (order.type === 'appointment') {
        setInvoiceLinks(null);
        return;
      }
      setInvoiceLinksLoading(true);
      try {
        const response = await axios.get(`/invoices/${order.id}/links`);
        if (response.data.success) setInvoiceLinks(response.data.links);
      } catch (err) {
        // silent
      } finally {
        setInvoiceLinksLoading(false);
      }
    };
    fetchLinks();
  }, [order?.id, order?.type]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const onClick = (e) => {
      if (!e.target.closest('[data-dropdown]')) {
        setStatusOpen(false);
        setPaymentOpen(false);
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
  }, []);

  if (!order) {
    return (
      <Shell>
        <div className="rsord-detail">
          <div className="rsord-detail__state">
            <span className="rsord-detail__spinner" />
            <span>Loading order…</span>
          </div>
        </div>
      </Shell>
    );
  }

  if (!localOrder) return null;

  const isAppointment = localOrder.type === 'appointment';
  const isChowdeckDelivery = localOrder?.metadata?.deliveryMethod === 'CHOWDECK';
  const isChowdeckDispatched = !!localOrder?.metadata?.chowdeck?.deliveryId;
  const canDispatchChowdeck = isChowdeckDelivery && !isChowdeckDispatched && localOrder?.paymentStatus?.toLowerCase() === 'paid';
  const isTerminalDelivery = localOrder?.metadata?.deliveryMethod === 'TERMINAL_AFRICA';
  const terminal = localOrder?.metadata?.terminalAfrica || {};
  const terminalCarrier = [terminal.carrierName, terminal.rate?.carrierName].find(name =>
    typeof name === 'string' && name.trim() && !/^unknown(?:\s+(?:carrier|courier))?$/i.test(name.trim())) || 'Nationwide delivery';
  const isTerminalDispatched = !!localOrder?.metadata?.terminalAfrica?.shipmentId;
  const canDispatchTerminal = isTerminalDelivery && !isTerminalDispatched && localOrder?.paymentStatus?.toLowerCase() === 'paid';
  const normalizedOrderStatus = localOrder.status?.toLowerCase();
  const normalizedPaymentStatus = localOrder.paymentStatus?.toLowerCase();
  const canRefund =
    !isAppointment &&
    normalizedPaymentStatus === 'paid' &&
    !['shipped', 'delivered', 'completed', 'cancelled', 'refunded'].includes(normalizedOrderStatus) &&
    normalizedPaymentStatus !== 'refunded';

  const handleStatusUpdate = async (newStatus) => {
    setLocalOrder(prev => ({ ...prev, status: newStatus }));
    setStatusOpen(false);
    try {
      await dispatch(updateOrderStatus({ orderId: localOrder.id, status: newStatus, token }));
      dispatch(refreshAnalytics({ token, period: 'month' })).catch(() => {});
      toast.success('Order status updated');
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handlePaymentUpdate = async (newPaymentStatus) => {
    setLocalOrder(prev => ({ ...prev, paymentStatus: newPaymentStatus }));
    setPaymentOpen(false);
    try {
      await dispatch(updatePaymentStatus({ orderId: localOrder.id, paymentStatus: newPaymentStatus }));
      toast.success('Payment status updated');
    } catch (err) {
      toast.error('Failed to update payment status');
    }
  };

  const handleRiderAssignment = () => {
    const riderData = {
      riderName: riderInfo.name,
      riderPhone: riderInfo.phone,
      riderEmail: riderInfo.notes,
      riderCompany: riderInfo.company,
      trackingCode: riderInfo.trackingCode,
      trackingUrl: riderInfo.trackingUrl
    };
    dispatch(assignRider({ orderId: localOrder.id, riderInfo: riderData, token }));
    setShowRiderForm(false);
    setRiderInfo({ name: '', phone: '', notes: '', company: '', trackingCode: '', trackingUrl: '' });
    toast.success('Rider assigned');
  };

  const handleTerminalDispatch = async () => {
    setDispatchingTerminal(true);
    setTerminalError(null);
    try {
      const response = await axios.post(`/terminal-africa/orders/${localOrder.id}/dispatch`);
      if (response.data.success) {
        const updated = {
          ...localOrder,
          ...response.data.order,
          status: response.data.order?.status || 'PROCESSING',
          metadata: {
            ...localOrder.metadata,
            ...response.data.order?.metadata,
            terminalAfrica: {
              ...terminal,
              shipmentId: response.data.shipmentId,
              trackingNumber: response.data.trackingNumber,
              trackingUrl: response.data.trackingUrl,
              carrierName: response.data.carrierName || terminalCarrier,
              labelUrl: response.data.labelUrl,
              status: 'DISPATCHED',
              dispatchedAt: response.data.dispatchedAt,
              ...response.data.order?.metadata?.terminalAfrica,
            },
          },
        };
        setLocalOrder(updated);
        dispatch(orderUpdated(updated));
        toast.success(`Pickup requested with ${updated.metadata.terminalAfrica.carrierName}`);
      } else {
        setTerminalError(response.data.error || 'Failed to book shipment');
      }
    } catch (err) {
      setTerminalError(err.response?.data?.error || 'Failed to book shipment');
    } finally {
      setDispatchingTerminal(false);
    }
  };

  const handleTerminalRefresh = async () => {
    setRefreshingTerminal(true);
    setTerminalError(null);
    try {
      const response = await axios.post(`/terminal-africa/orders/${localOrder.id}/refresh-tracking`);
      if (!response.data.success || !response.data.order) throw new Error('Could not refresh tracking.');
      setLocalOrder(prev => ({ ...prev, ...response.data.order }));
      dispatch(orderUpdated(response.data.order));
    } catch (error) {
      setTerminalError(error.response?.data?.error || 'Could not refresh tracking.');
    } finally {
      setRefreshingTerminal(false);
    }
  };

  const handleTerminalCancel = async () => {
    if (!window.confirm('Cancel this shipment? Your wallet will be refunded.')) return;
    setDispatchingTerminal(true);
    setTerminalError(null);
    try {
      const response = await axios.post(`/terminal-africa/orders/${localOrder.id}/cancel-shipment`);
      if (response.data.success) {
        const updated = {
          ...localOrder,
          metadata: {
            ...localOrder.metadata,
            terminalAfrica: {
              ...terminal,
              status: 'CANCELLED',
              cancelledAt: new Date().toISOString(),
              refundStatus: response.data.refund ? 'COMPLETED' : terminal.refundStatus,
            },
          },
        };
        setLocalOrder(updated);
        dispatch(orderUpdated(updated));
        toast.success(
          response.data.refund
            ? `Shipment cancelled — ₦${response.data.refund.amount.toLocaleString()} refunded to wallet`
            : 'Shipment cancelled',
        );
      } else {
        setTerminalError(response.data.error || 'Failed to cancel shipment');
      }
    } catch (err) {
      setTerminalError(err.response?.data?.error || 'Failed to cancel shipment');
    } finally {
      setDispatchingTerminal(false);
    }
  };

  const handleChowdeckDispatch = async () => {
    setDispatchingChowdeck(true);
    setChowdeckError(null);
    try {
      const response = await axios.post(`/chowdeck/orders/${localOrder.id}/dispatch`);
      if (response.data.success) {
        setLocalOrder(prev => ({
          ...prev,
          status: 'processing',
          metadata: {
            ...prev.metadata,
            chowdeck: {
              ...prev.metadata?.chowdeck,
              deliveryId: response.data.chowdeckReference,
              status: 'ORDER_CREATED',
              dispatchedAt: new Date().toISOString()
            }
          }
        }));
        toast.success('Chowdeck delivery dispatched');
      } else {
        setChowdeckError(response.data.error || 'Failed to dispatch delivery');
      }
    } catch (err) {
      setChowdeckError(err.response?.data?.error || 'Failed to dispatch Chowdeck delivery');
    } finally {
      setDispatchingChowdeck(false);
    }
  };

  const handleCopyLink = async (link, type) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(type);
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (err) {
      // ignored
    }
  };

  const handleSendEmail = async (type) => {
    setSendingEmail(true);
    setEmailError(null);
    setEmailSent(null);
    try {
      const endpoint = isAppointment
        ? `/appointments/${localOrder.id}/send-email`
        : `/invoices/${localOrder.id}/send-email`;
      const response = await axios.post(endpoint, { type });
      if (response.data.success) {
        setEmailSent(type);
        setTimeout(() => setEmailSent(null), 3000);
        toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} sent to customer`);
      } else {
        setEmailError(response.data.error || 'Failed to send email');
      }
    } catch (err) {
      if (err.response?.data?.noEmail) setEmailError('No customer email available');
      else setEmailError(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const customerName = localOrder.customer?.firstName || localOrder.customerInfo?.firstName
    ? `${localOrder.customer?.firstName || localOrder.customerInfo?.firstName} ${localOrder.customer?.lastName || localOrder.customerInfo?.lastName || ''}`.trim()
    : localOrder.customerInfo?.name || localOrder.customer?.name || 'Guest customer';
  const customerEmail = localOrder.customer?.email || localOrder.customerInfo?.email;
  const customerPhone = localOrder.customer?.phone || localOrder.customerInfo?.phone;
  const cleanPhone = customerPhone ? customerPhone.replace(/[^0-9+]/g, '') : '';

  const address =
    localOrder.shippingInfo?.address ||
    localOrder.shippingAddress?.street ||
    localOrder.shippingAddress?.address ||
    localOrder.customerInfo?.address ||
    '';
  const city =
    localOrder.shippingInfo?.city ||
    localOrder.shippingAddress?.city ||
    localOrder.customerInfo?.city ||
    '';
  const state =
    localOrder.shippingInfo?.state ||
    localOrder.shippingAddress?.state ||
    localOrder.customerInfo?.state ||
    '';
  const items_ = localOrder.orderItems?.length ? localOrder.orderItems : (localOrder.items || []);

  // Rider has real data only when name or phone is present
  const hasRider = !!(localOrder.rider && (localOrder.rider.name || localOrder.rider.phone));

  return (
    <Shell>
      <div className="rsord-detail">
        {/* Top bar */}
        <header className="rsord-detail__topbar">
          <Link to="/orders" className="rsord-detail__back">
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            <span>Orders</span>
          </Link>
          <div className="rsord-detail__topbar-actions">
            {(localOrder.paymentStatus || 'unpaid').toLowerCase() === 'unpaid' && !isAppointment && (
              <button
                type="button"
                className="rsord-detail__btn rsord-detail__btn--primary"
                onClick={(e) => setPaymentLinkAnchor(e.currentTarget)}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
                </svg>
                <span>Send payment link</span>
              </button>
            )}
            <button
              type="button"
              className="rsord-detail__btn rsord-detail__btn--ghost"
              onClick={() => window.print()}
              title="Print"
              aria-label="Print"
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z" />
              </svg>
              <span>Print</span>
            </button>
            {canRefund && (
              <button
                type="button"
                className="rsord-detail__btn rsord-detail__btn--ghost"
                onClick={() => setShowRefundModal(true)}
                title="Refund"
                aria-label="Refund"
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M3 10h10a5 5 0 015 5v4" />
                  <polyline points="3 10 7 6 3 10 7 14" />
                </svg>
                <span>Refund</span>
              </button>
            )}
          </div>
        </header>

        {/* Manual Bank Transfer — awaiting confirmation banner (Sprint 5).
            Surfaces the state that comes from the customer tapping "I've
            paid" on the storefront. Merchant either sees the auto-match
            resolve within a minute, or reconciles by verifying in their
            own bank app + flipping the payment pill to Paid. */}
        {(() => {
          const isUnpaid = (localOrder.paymentStatus || 'unpaid').toLowerCase() === 'unpaid';
          const isBank = (localOrder.paymentMethod || '').toString().toUpperCase() === 'BANK_TRANSFER';
          const mt = localOrder.metadata?.manualTransfer;
          const awaiting = isUnpaid && isBank && mt?.initiated === true;
          if (!awaiting) return null;
          const initiatedAt = mt?.initiatedAt ? new Date(mt.initiatedAt) : null;
          const waitingMinutes = initiatedAt
            ? Math.max(0, Math.floor((Date.now() - initiatedAt.getTime()) / 60000))
            : null;
          return (
            <div className="rsord-detail__mt-banner">
              <div className="rsord-detail__mt-banner-icon" aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 16 14" />
                </svg>
              </div>
              <div className="rsord-detail__mt-banner-body">
                <p className="rsord-detail__mt-banner-title">Customer says they've paid — waiting for the bank transfer to land</p>
                <p className="rsord-detail__mt-banner-text">
                  {waitingMinutes !== null
                    ? `Initiated ${waitingMinutes} minute${waitingMinutes === 1 ? '' : 's'} ago. `
                    : ''}
                  Auto-match runs on every Paystack Terminal deposit. If the transfer already landed in your VT bank app, mark this order as Paid using the Payment pill on the right.
                </p>
              </div>
            </div>
          );
        })()}

        {/* Header */}
        <section className="rsord-detail__header">
          <div className="rsord-detail__heading">
            <div className="rsord-detail__title-row">
              <h1 className="rsord-detail__title">Order #{localOrder.id.slice(-8)}</h1>
              <span className={`rsord-detail__type-chip ${isAppointment ? 'is-appointment' : 'is-order'}`}>
                {isAppointment ? 'Appointment' : 'Order'}
              </span>
            </div>
            <p className="rsord-detail__meta">
              {new Date(localOrder.createdAt).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
              {localOrder.channel && !isAppointment && (
                <>
                  {' · '}
                  <span className="rsord-detail__channel">{localOrder.channel.toLowerCase()}</span>
                </>
              )}
            </p>
          </div>

          <div className="rsord-detail__pills">
            {/* Status pill (interactive) */}
            <div className="rsord-detail__pill-group" data-dropdown>
              <button
                type="button"
                className="rsord-detail__pill-trigger"
                onClick={(e) => { e.stopPropagation(); setStatusOpen(!statusOpen); setPaymentOpen(false); }}
              >
                <span className="rsord-detail__pill-label">Status</span>
                <span className={`rsord-status rsord-status--${getStatusClass(localOrder.status)}`}>
                  {getStatusLabel(localOrder.status)}
                </span>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ transform: statusOpen ? 'rotate(180deg)' : 'none' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {statusOpen && (
                <div className="rsord-detail__pill-menu">
                  {STATUS_OPTIONS.map(s => (
                    <button key={s} type="button" className="rsord-detail__pill-option" onClick={() => handleStatusUpdate(s)}>
                      <span className={`rsord-status rsord-status--${s}`}>{getStatusLabel(s)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Payment pill (interactive) */}
            <div className="rsord-detail__pill-group" data-dropdown>
              <button
                type="button"
                className="rsord-detail__pill-trigger"
                onClick={(e) => { e.stopPropagation(); setPaymentOpen(!paymentOpen); setStatusOpen(false); }}
              >
                <span className="rsord-detail__pill-label">Payment</span>
                <span className={`rsord-payment rsord-payment--${getPaymentClass(localOrder.paymentStatus)}`}>
                  {getStatusLabel(localOrder.paymentStatus)}
                </span>
                <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ transform: paymentOpen ? 'rotate(180deg)' : 'none' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {paymentOpen && (
                <div className="rsord-detail__pill-menu">
                  {PAYMENT_OPTIONS.map(p => (
                    <button key={p} type="button" className="rsord-detail__pill-option" onClick={() => handlePaymentUpdate(p)}>
                      <span className={`rsord-payment rsord-payment--${p}`}>{getStatusLabel(p)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {(localOrder.discountCode || localOrder.discountTotal > 0) && (
              <div className="rsord-detail__pill-group">
                <span className="rsord-detail__pill-label">Discount</span>
                <span className="rsord-detail__discount-pill">{localOrder.discountCode || 'Applied'}</span>
              </div>
            )}
          </div>
        </section>

        {/* Grid */}
        <div className="rsord-detail__grid">
          {/* Main column */}
          <div className="rsord-detail__main">
            {/* Products */}
            <section className="rsord-detail__card">
              <div className="rsord-detail__card-head">
                <h2 className="rsord-detail__card-title">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                  </svg>
                  Products
                  <span className="rsord-detail__count">{items_.length}</span>
                </h2>
                {localOrder.channel === 'POS' && localOrder.soldByName && (
                  <span className="rsord-detail__muted">Sold by {localOrder.soldByName}</span>
                )}
              </div>

              <ul className="rsord-detail__products">
                {items_.length === 0 && (
                  <li className="rsord-detail__empty">No items</li>
                )}
                {items_.map((item, idx) => {
                  const imgSrc = item.product?.primaryImage || item.image;
                  const name = item.product?.name || item.name || 'Product';
                  const qty = item.qty || item.quantity || 1;
                  const total = item.total || (item.price * qty);
                  const unitInfo = formatUnitInfo(item, formatMoney);
                  const variant = formatVariantDisplay(item);
                  return (
                    <li key={idx} className="rsord-detail__product">
                      <div className="rsord-detail__product-thumb">
                        {imgSrc ? <img src={imgSrc} alt={name} /> : (
                          <svg width="18" height="18" fill="none" stroke="#9ca3af" strokeWidth="1.5" viewBox="0 0 24 24">
                            <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" />
                          </svg>
                        )}
                      </div>
                      <div className="rsord-detail__product-info">
                        <span className="rsord-detail__product-name">{name}</span>
                        {variant && <span className="rsord-detail__product-variant">{variant}</span>}
                        <span className="rsord-detail__product-meta">
                          {unitInfo || `${qty} × ${formatMoney(total && qty ? total / qty : item.price)}`}
                        </span>
                      </div>
                      <span className="rsord-detail__product-total">{formatMoney(total)}</span>
                    </li>
                  );
                })}
              </ul>

              {/* Summary */}
              <div className="rsord-detail__summary">
                <div className="rsord-detail__summary-row">
                  <span>Subtotal</span>
                  <span>{formatMoney(localOrder.subtotal || 0)}</span>
                </div>
                {(localOrder.discountTotal > 0 || localOrder.discountCode) && (
                  <div className="rsord-detail__summary-row rsord-detail__summary-row--discount">
                    <span>Discount {localOrder.discountCode && <em>({localOrder.discountCode})</em>}</span>
                    <span>-{formatMoney(localOrder.discountTotal || 0)}</span>
                  </div>
                )}
                {(localOrder.taxTotal > 0 || localOrder.vat > 0) && (
                  <div className="rsord-detail__summary-row">
                    <span>{getOrderTaxName(localOrder)}</span>
                    <span>{formatMoney(localOrder.taxTotal || localOrder.vat || 0)}</span>
                  </div>
                )}
                {localOrder.serviceCharge > 0 && (
                  <div className="rsord-detail__summary-row">
                    <span>Service charge</span>
                    <span>{formatMoney(localOrder.serviceCharge || 0)}</span>
                  </div>
                )}
                {(localOrder.shippingFee > 0 || localOrder.deliveryFee > 0) && (
                  <div className="rsord-detail__summary-row">
                    <span>Delivery</span>
                    <span>{formatMoney(localOrder.shippingFee || localOrder.deliveryFee || 0)}</span>
                  </div>
                )}
                <div className="rsord-detail__summary-row rsord-detail__summary-row--total">
                  <span>Total</span>
                  <span>{formatMoney(localOrder.total)}</span>
                </div>

                {/* Profitability — owners + finance:view only. COGS + gross
                    profit are frozen at sale time (Phase 1.2). Null means
                    the order was placed before COGS wiring shipped. */}
                {canSeeProfitability && (localOrder.totalCogs != null || localOrder.grossProfit != null) && (() => {
                  const cogs = Number(localOrder.totalCogs || 0);
                  const profit = Number(localOrder.grossProfit || 0);
                  const revenue = Number(localOrder.subtotal || localOrder.total || 0);
                  const marginPct = revenue > 0 ? (profit / revenue) * 100 : 0;
                  const marginTone = marginPct >= 30 ? '#047857'
                                  : marginPct >= 10 ? '#a16207'
                                  : '#b91c1c';
                  return (
                    <>
                      <div
                        className="rsord-detail__summary-row"
                        style={{
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: '1px dashed #e5e7eb',
                          color: '#6b7280',
                          fontSize: 12,
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        <span>Profitability</span>
                        <span aria-hidden></span>
                      </div>
                      <div className="rsord-detail__summary-row">
                        <span>Cost of goods</span>
                        <span>{formatMoney(cogs)}</span>
                      </div>
                      <div className="rsord-detail__summary-row">
                        <span>Gross profit</span>
                        <span>{formatMoney(profit)}</span>
                      </div>
                      <div className="rsord-detail__summary-row">
                        <span>Margin</span>
                        <span style={{ color: marginTone, fontWeight: 600 }}>
                          {marginPct.toFixed(1)}%
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </section>

            {/* Documents */}
            <section className="rsord-detail__card">
              <div className="rsord-detail__card-head">
                <h2 className="rsord-detail__card-title">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  Documents
                </h2>
              </div>

              {invoiceLinksLoading ? (
                <div className="rsord-detail__doc-loading">Loading…</div>
              ) : (
                <div className="rsord-detail__docs">
                  {/* Invoice */}
                  <div className="rsord-detail__doc">
                    <div className="rsord-detail__doc-info">
                      <span className="rsord-detail__doc-icon">
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                      </span>
                      <span className="rsord-detail__doc-name">Invoice</span>
                    </div>
                    <div className="rsord-detail__doc-actions">
                      {!isAppointment && invoiceLinks?.invoice && (
                        <>
                          <a href={invoiceLinks.invoice} target="_blank" rel="noopener noreferrer" className="rsord-detail__doc-btn">View</a>
                          <button type="button" className={`rsord-detail__doc-btn ${copiedLink === 'invoice' ? 'is-success' : ''}`} onClick={() => handleCopyLink(invoiceLinks.invoice, 'invoice')}>
                            {copiedLink === 'invoice' ? 'Copied' : 'Copy'}
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        className={`rsord-detail__doc-btn rsord-detail__doc-btn--primary ${emailSent === 'invoice' ? 'is-success' : ''}`}
                        onClick={() => handleSendEmail('invoice')}
                        disabled={sendingEmail}
                      >
                        {sendingEmail ? '…' : emailSent === 'invoice' ? 'Sent' : 'Send'}
                      </button>
                    </div>
                  </div>

                  {/* Receipt */}
                  {localOrder.paymentStatus?.toLowerCase() === 'paid' ? (
                    <div className="rsord-detail__doc">
                      <div className="rsord-detail__doc-info">
                        <span className="rsord-detail__doc-icon rsord-detail__doc-icon--success">
                          <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24">
                            <path d="M4 4a2 2 0 012-2h8l6 6v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" />
                            <path d="M9 15l2 2 4-4" />
                          </svg>
                        </span>
                        <span className="rsord-detail__doc-name">Receipt</span>
                      </div>
                      <div className="rsord-detail__doc-actions">
                        {!isAppointment && invoiceLinks?.receipt && (
                          <>
                            <a href={invoiceLinks.receipt} target="_blank" rel="noopener noreferrer" className="rsord-detail__doc-btn">View</a>
                            <button type="button" className={`rsord-detail__doc-btn ${copiedLink === 'receipt' ? 'is-success' : ''}`} onClick={() => handleCopyLink(invoiceLinks.receipt, 'receipt')}>
                              {copiedLink === 'receipt' ? 'Copied' : 'Copy'}
                            </button>
                          </>
                        )}
                        <button
                          type="button"
                          className={`rsord-detail__doc-btn rsord-detail__doc-btn--primary ${emailSent === 'receipt' ? 'is-success' : ''}`}
                          onClick={() => handleSendEmail('receipt')}
                          disabled={sendingEmail}
                        >
                          {sendingEmail ? '…' : emailSent === 'receipt' ? 'Sent' : 'Send'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="rsord-detail__doc-notice">Receipt available after payment</div>
                  )}

                  {emailError && <div className="rsord-detail__doc-error">{emailError}</div>}
                </div>
              )}
            </section>
          </div>

          {/* Side column */}
          <aside className="rsord-detail__side">
            {/* Customer */}
            <section className="rsord-detail__card">
              <div className="rsord-detail__card-head">
                <h2 className="rsord-detail__card-title">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                  </svg>
                  Customer
                </h2>
              </div>

              <div className="rsord-detail__customer">
                <div className="rsord-detail__customer-row">
                  <div className="rsord-detail__avatar">{(customerName || 'G')[0].toUpperCase()}</div>
                  <div className="rsord-detail__customer-info">
                    <span className="rsord-detail__customer-name">{customerName}</span>
                    {customerEmail && <span className="rsord-detail__customer-email">{customerEmail}</span>}
                  </div>
                </div>
                {customerPhone && (
                  <div className="rsord-detail__customer-line">
                    <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                    </svg>
                    {customerPhone}
                  </div>
                )}

                {(customerPhone || customerEmail) && (
                  <div className="rsord-detail__contact-row">
                    {customerPhone && (
                      <a href={`tel:${cleanPhone}`} className="rsord-detail__contact-btn">
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
                        </svg>
                        Call
                      </a>
                    )}
                    {customerPhone && (
                      <a href={`https://wa.me/${cleanPhone.replace(/^\+/, '')}`} target="_blank" rel="noopener noreferrer" className="rsord-detail__contact-btn rsord-detail__contact-btn--wa">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                        </svg>
                        WhatsApp
                      </a>
                    )}
                    {customerEmail && (
                      <a href={`mailto:${customerEmail}`} className="rsord-detail__contact-btn">
                        <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        Email
                      </a>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Delivery */}
            <section className="rsord-detail__card">
              <div className="rsord-detail__card-head">
                <h2 className="rsord-detail__card-title">
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  Delivery
                </h2>
              </div>

              <div className="rsord-detail__address">
                {address ? (
                  <>
                    <p className="rsord-detail__address-line">{address}</p>
                    <p className="rsord-detail__address-city">{[city, state].filter(Boolean).join(', ')}</p>
                  </>
                ) : (
                  <p className="rsord-detail__address-empty">No address provided</p>
                )}
              </div>

              {address && (
                <div className="rsord-detail__map">
                  <iframe
                    title="Delivery location map"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps?q=${encodeURIComponent(`${address}, ${city || ''}, ${state || ''}`)}&output=embed`}
                  />
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${address}, ${city || ''}, ${state || ''}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rsord-detail__map-link"
                  >
                    Open in Maps
                  </a>
                </div>
              )}

              {/* Rider section */}
              {!isAppointment && (
                <div className="rsord-detail__rider">
                  <div className="rsord-detail__rider-head">
                    <span className="rsord-detail__rider-title">
                      Rider
                      {isChowdeckDelivery && <span className="rsord-detail__chowdeck-tag">Chowdeck</span>}
                    </span>
                    {!isChowdeckDelivery && !hasRider && (
                      <button type="button" className="rsord-detail__text-btn" onClick={() => setShowRiderForm(!showRiderForm)}>
                        {showRiderForm ? 'Cancel' : '+ Assign'}
                      </button>
                    )}
                  </div>

                  {/* Chowdeck dispatch UI (only when no rider assigned yet) */}
                  {isChowdeckDelivery && !hasRider && (
                    <>
                      {canDispatchChowdeck && (
                        <>
                          {chowdeckError && <div className="rsord-detail__inline-error">{chowdeckError}</div>}
                          <button
                            type="button"
                            className="rsord-detail__btn rsord-detail__btn--chowdeck"
                            onClick={handleChowdeckDispatch}
                            disabled={dispatchingChowdeck}
                          >
                            {dispatchingChowdeck ? 'Dispatching…' : 'Dispatch via Chowdeck'}
                          </button>
                        </>
                      )}
                      {isChowdeckDispatched && (
                        <div className="rsord-detail__chowdeck-info">
                          <strong>Dispatched, waiting for rider</strong>
                          <span>ID: {localOrder?.metadata?.chowdeck?.deliveryId}</span>
                          {localOrder?.metadata?.chowdeck?.status && (
                            <span>Status: {localOrder.metadata.chowdeck.status}</span>
                          )}
                        </div>
                      )}
                      {!canDispatchChowdeck && !isChowdeckDispatched && (
                        <p className="rsord-detail__muted">Order must be paid before dispatching.</p>
                      )}
                    </>
                  )}

                  {/* Terminal Africa dispatch UI */}
                  {isTerminalDelivery && (
                    <>
                      {canDispatchTerminal && (
                        <>
                          {terminalError && <div className="rsord-detail__inline-error">{terminalError}</div>}
                          <button
                            type="button"
                            className="rsord-detail__btn rsord-detail__btn--chowdeck"
                            onClick={handleTerminalDispatch}
                            disabled={dispatchingTerminal}
                          >
                            {dispatchingTerminal ? 'Booking…' : `Ship via ${terminalCarrier}`}
                          </button>
                        </>
                      )}
                      {isTerminalDispatched && localOrder?.metadata?.terminalAfrica?.status !== 'CANCELLED' && (
                        <div className="rsord-detail__chowdeck-info">
                          <strong>{terminalCarrier} — {['shipped', 'delivered'].includes(normalizedOrderStatus) ? getStatusLabel(normalizedOrderStatus) : 'pickup requested'}</strong>
                          <span>{terminal.trackingNumber ? `Tracking: ${terminal.trackingNumber}` : 'Tracking code pending from courier'}</span>
                          {/^https?:\/\//i.test(terminal.trackingUrl || '') && (
                            <a href={terminal.trackingUrl} target="_blank" rel="noopener noreferrer" className="rsord-detail__text-btn">Track delivery</a>
                          )}
                          <span>Shipment: {localOrder.metadata.terminalAfrica.shipmentId}</span>
                          {canManageOrders && <button type="button" className="rsord-detail__text-btn" onClick={handleTerminalRefresh} disabled={refreshingTerminal || dispatchingTerminal}>
                            {refreshingTerminal ? 'Refreshing…' : 'Refresh tracking'}
                          </button>}
                          {localOrder?.metadata?.terminalAfrica?.labelUrl && (
                            <a
                              href={localOrder.metadata.terminalAfrica.labelUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="rsord-detail__text-btn"
                            >
                              Download waybill
                            </a>
                          )}
                          {terminalError && <div className="rsord-detail__inline-error">{terminalError}</div>}
                          {!['shipped', 'delivered'].includes(normalizedOrderStatus) && <button
                            type="button"
                            className="rsord-detail__text-btn"
                            onClick={handleTerminalCancel}
                            disabled={dispatchingTerminal || refreshingTerminal}
                          >
                            {dispatchingTerminal ? 'Cancelling…' : 'Cancel shipment'}
                          </button>}
                        </div>
                      )}
                      {localOrder?.metadata?.terminalAfrica?.status === 'CANCELLED' && (
                        <p className="rsord-detail__muted">{terminal.refundStatus === 'FAILED' ? 'Shipment cancelled. Wallet refund needs support review.' : 'Shipment cancelled.'}</p>
                      )}
                      {!canDispatchTerminal && !isTerminalDispatched && (
                        <p className="rsord-detail__muted">Order must be paid before dispatching.</p>
                      )}
                    </>
                  )}

                  {/* Manual rider form */}
                  {!isChowdeckDelivery && showRiderForm && (
                    <div className="rsord-detail__rider-form">
                      <div className="rsord-detail__field-grid">
                        <input type="text" value={riderInfo.name} onChange={(e) => setRiderInfo({ ...riderInfo, name: e.target.value })} placeholder="Rider name *" className="rsord-detail__input" />
                        <input type="tel" value={riderInfo.phone} onChange={(e) => setRiderInfo({ ...riderInfo, phone: e.target.value })} placeholder="Rider phone *" className="rsord-detail__input" />
                      </div>
                      <input type="text" value={riderInfo.company} onChange={(e) => setRiderInfo({ ...riderInfo, company: e.target.value })} placeholder="Delivery company" className="rsord-detail__input" />
                      <div className="rsord-detail__field-grid">
                        <input type="text" value={riderInfo.trackingCode} onChange={(e) => setRiderInfo({ ...riderInfo, trackingCode: e.target.value })} placeholder="Tracking code" className="rsord-detail__input" />
                        <input type="url" value={riderInfo.trackingUrl} onChange={(e) => setRiderInfo({ ...riderInfo, trackingUrl: e.target.value })} placeholder="Tracking URL" className="rsord-detail__input" />
                      </div>
                      <textarea value={riderInfo.notes} onChange={(e) => setRiderInfo({ ...riderInfo, notes: e.target.value })} placeholder="Delivery notes…" rows={2} className="rsord-detail__textarea" />
                      <button
                        type="button"
                        className="rsord-detail__btn rsord-detail__btn--primary"
                        onClick={handleRiderAssignment}
                        disabled={!riderInfo.name || !riderInfo.phone}
                      >
                        Assign rider
                      </button>
                    </div>
                  )}

                  {/* Assigned rider card (manual or chowdeck once rider name lands) */}
                  {hasRider && (
                    <div className={`rsord-detail__rider-info ${isChowdeckDelivery ? 'is-chowdeck' : ''}`}>
                      <div className="rsord-detail__rider-info-head">
                        <strong>{localOrder.rider.name}</strong>
                        {(localOrder.rider.company || isChowdeckDelivery) && (
                          <span className="rsord-detail__rider-company">
                            {localOrder.rider.company || 'Chowdeck'}
                          </span>
                        )}
                      </div>
                      {localOrder.rider.phone && (
                        <span className="rsord-detail__rider-line">{localOrder.rider.phone}</span>
                      )}
                      {localOrder.rider.trackingCode && (
                        <span className="rsord-detail__rider-line">
                          Tracking:{' '}
                          {localOrder.rider.trackingUrl ? (
                            <a href={localOrder.rider.trackingUrl} target="_blank" rel="noopener noreferrer">{localOrder.rider.trackingCode}</a>
                          ) : (
                            <em>{localOrder.rider.trackingCode}</em>
                          )}
                        </span>
                      )}
                      {localOrder.rider.notes && <span className="rsord-detail__rider-notes">{localOrder.rider.notes}</span>}
                      <span className="rsord-detail__rider-foot">
                        <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        Customer was emailed with rider details
                      </span>
                    </div>
                  )}
                </div>
              )}
            </section>
          </aside>
        </div>

        {/* Phase 3.3 — payment links sent for this order. The card hides
            itself when the list is empty, so order detail isn't cluttered
            for orders that never went through a link. */}
        {!isAppointment && (
          <PaymentLinksHistory orderId={localOrder.id} refreshKey={historyKey} />
        )}

        {/* Phase 3.3 — Send-payment-link popover anchored to the topbar
            button. Refreshes the history when a link is created. */}
        <SendPaymentLinkPopover
          isOpen={!!paymentLinkAnchor}
          onClose={() => setPaymentLinkAnchor(null)}
          anchorEl={paymentLinkAnchor}
          order={localOrder}
          onLinkCreated={() => setHistoryKey((k) => k + 1)}
        />

        <RefundModal
          isOpen={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          order={localOrder}
          onRefundSuccess={() => {
            setShowRefundModal(false);
            dispatch(fetchOrders({ page: 1 }));
            navigate('/orders');
          }}
        />
      </div>
    </Shell>
  );
}
